import "@aws-amplify/ui-react/styles.css";
import "@mdxeditor/editor/style.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import "./main.css";

import { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";

import { ProvideAuth, useAuth } from "@/AuthContext";
import { Authenticator } from "@aws-amplify/ui-react";

import { ChakraProvider, Spinner, defaultSystem } from "@chakra-ui/react";

import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { APIProvider } from "@vis.gl/react-google-maps";
import LogRocket from "logrocket";
import posthog from "posthog-js";
import { Navigate, Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import ErrorPage from "./pages/error";
import Layout from "./pages/layout";
import { normalizeRoute } from "./utils/route";

const sentryEnvs = {
  development: "dev",
  production: "prod",
  staging: "staging",
};

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  environment: sentryEnvs[import.meta.env.VITE_APP_ENV],
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/api\.planharmony\.com\//, /^https:\/\/dev-api\.planharmony\.com\//],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if (import.meta.env.VITE_APP_LOGROCKET_ID) {
  LogRocket.init(import.meta.env.VITE_APP_LOGROCKET_ID);
}

// Initialize PostHog analytics
if (import.meta.env.VITE_APP_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_APP_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_APP_POSTHOG_API_HOST,
    ui_host: import.meta.env.VITE_APP_POSTHOG_UI_HOST,
    before_send: (event) => {
      if (!event?.properties) return event;
      const props = event.properties;
      if (props.$pathname) {
        props.$pathname = normalizeRoute(props.$pathname);
      }
      if (props.$current_url) {
        try {
          const u = new URL(props.$current_url);
          u.pathname = normalizeRoute(u.pathname);
          props.$current_url = u.toString();
        } catch {
          // leave as-is if not a valid URL
        }
      }
      return event;
    },
  });
}

const queryClient = new QueryClient();
const Login = lazy(() => import("@pages/login"));

const TripLayout = lazy(() => import("@pages/trips/trip/layout"));
const TripList = lazy(() => import("@pages/trips/list"));

const TripCalendar = lazy(() => import("@pages/trips/trip/calendar"));
const TripBudget = lazy(() => import("@pages/trips/trip/budget"));
const TripMap = lazy(() => import("@pages/trips/trip/map"));
const TripSettings = lazy(() => import("@pages/trips/trip/settings"));
const TripExperiences = lazy(() => import("@pages/trips/trip/experiences"));
const TripLodging = lazy(() => import("@pages/trips/trip/lodging"));
const TripUpgrade = lazy(() => import("@pages/trips/trip/upgrade"));
const TripUpgradeSuccess = lazy(() => import("@pages/trips/trip/upgrade.success"));
const TripFiles = lazy(() => import("@pages/trips/trip/files"));
const TripPreferences = lazy(() => import("@pages/trips/trip/preferences"));
const TripAgentProfile = lazy(() => import("@pages/trips/trip/agent-profile"));

const PublicTripLayout = lazy(() => import("@pages/trips/public/layout"));
const PublicTripCalendar = lazy(() => import("@pages/trips/public/calendar"));
const PublicTripMap = lazy(() => import("@pages/trips/public/map"));

const ExploreList = lazy(() => import("@pages/explore/list"));
const Experiences = lazy(() => import("@pages/experiences"));

const Settings = lazy(() => import("@pages/user/settings"));
const Subscribe = lazy(() => import("@pages/user/subscribe"));
const SubscribeSuccess = lazy(() => import("@pages/user/subscribe.success"));

const AgencySignup = lazy(() => import("@pages/agency/signup"));
const AgencyLayout = lazy(() => import("@pages/agency/layout"));
const AgencyDashboard = lazy(() => import("@pages/agency/dashboard"));
const AgencyClients = lazy(() => import("@pages/agency/clients/list"));
const AgencyClientDetail = lazy(() => import("@pages/agency/clients/detail"));
const AgencySettings = lazy(() => import("@pages/agency/settings"));
const AgencyTeam = lazy(() => import("@pages/agency/team"));
const AgencyInquiries = lazy(() => import("@pages/agency/inquiries/list"));
const AgencyInquiryDetail = lazy(() => import("@pages/agency/inquiries/detail"));
const AgencyBlogList = lazy(() => import("@pages/agency/blog/list"));
const AgencyBlogEditor = lazy(() => import("@pages/agency/blog/editor"));
const AgencyBlogPublicDetail = lazy(() => import("@pages/agency/blog/public-detail"));
const AgencyInvoices = lazy(() => import("@pages/agency/invoices/list"));
const AgencyInvoiceDetail = lazy(() => import("@pages/agency/invoices/detail"));
const AgencyForms = lazy(() => import("@pages/agency/forms/list"));
const AgencyFormEditor = lazy(() => import("@pages/agency/forms/editor"));
const AgencyFormResponses = lazy(() => import("@pages/agency/forms/responses"));
const AgencyFormResponseDetail = lazy(() => import("@pages/agency/forms/responses/detail"));
const AgencyPayments = lazy(() => import("@pages/agency/payments/list"));
const AgencyPaymentDetail = lazy(() => import("@pages/agency/payments/detail"));
const AgencyCommissions = lazy(() => import("@pages/agency/commissions/list"));
const AgencyCommissionDetail = lazy(() => import("@pages/agency/commissions/detail"));
const AgencyTasks = lazy(() => import("@pages/agency/tasks/list"));
const AgencySocialList = lazy(() => import("@pages/agency/social/list"));
const AgencySocialEditor = lazy(() => import("@pages/agency/social/editor"));
const PublicFormSubmit = lazy(() => import("@pages/agency/forms/public-submit"));
const PaymentCheckout = lazy(() => import("@pages/pay/checkout"));
const PaymentConfirmation = lazy(() => import("@pages/pay/confirmation"));
const AgencyDirectory = lazy(() => import("@pages/agency/directory"));
const AgencyProfile = lazy(() => import("@pages/agency/profile"));
const CustomDomainProfile = lazy(() => import("@pages/agency/profile/custom-domain"));

const hostname = window.location.hostname;
const isCustomDomain = !(hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".planharmony.com"));

const RequireAuth = ({ children }) => {
  const { authStatus } = useAuth();

  if (authStatus === "unauthenticated") return <Navigate to="/login" replace />;
  if (authStatus === "configuring") return <Spinner size="sm" m={3} />;

  return <Suspense fallback={<Spinner size="md" m={3} />}>{children}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Outlet />} errorElement={<ErrorPage />}>
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }>
        <Route index element={<Navigate to="trips" />} />
        <Route path="trips" element={<Outlet />}>
          <Route index element={<TripList />} />
          <Route path=":id" element={<TripLayout />}>
            <Route index element={<Navigate to="calendar" />} />
            <Route index path="calendar/:eventId?" element={<TripCalendar />} />
            <Route path="budget" element={<TripBudget />} />
            <Route path="map" element={<TripMap />} />
            <Route path="experiences" element={<TripExperiences />} />
            <Route path="lodging" element={<TripLodging />} />
            <Route path="settings" element={<TripSettings />} />
            <Route path="upgrade" element={<TripUpgrade />} />
            <Route path="upgrade/success" element={<TripUpgradeSuccess />} />
            <Route path="files" element={<TripFiles />} />
            <Route path="preferences" element={<TripPreferences />} />
            <Route path="agent" element={<TripAgentProfile />} />
          </Route>
        </Route>
        <Route path="settings" element={<Settings />} />
        <Route path="subscribe" element={<Subscribe />} />
        <Route path="subscribe/success" element={<SubscribeSuccess />} />
        <Route path="explore" element={<Outlet />}>
          <Route index element={<ExploreList />} />
        </Route>
        <Route path="experiences" element={<Experiences />} />
        <Route path="agency" element={<Outlet />}>
          <Route path="directory" element={<AgencyDirectory />} />
          <Route path="profile/:agencyId" element={<Outlet />}>
            <Route index element={<AgencyProfile />} />
            <Route path="blog" element={<AgencyProfile />} />
            <Route path="blog/:postSlug" element={<AgencyBlogPublicDetail />} />
            <Route path="form/:formId" element={<PublicFormSubmit />} />
          </Route>
          <Route path="signup" element={<AgencySignup />} />
          <Route element={<AgencyLayout />}>
            <Route index element={<AgencyDashboard />} />
            <Route path="dashboard" element={<AgencyDashboard />} />
            <Route path="clients" element={<Outlet />}>
              <Route index element={<AgencyClients />} />
              <Route path=":clientId" element={<AgencyClientDetail />} />
            </Route>
            <Route path="inquiries" element={<Outlet />}>
              <Route index element={<AgencyInquiries />} />
              <Route path=":inquiryId" element={<AgencyInquiryDetail />} />
            </Route>
            <Route path="tasks" element={<AgencyTasks />} />
            <Route path="invoices" element={<Outlet />}>
              <Route index element={<AgencyInvoices />} />
              <Route path=":invoiceId" element={<AgencyInvoiceDetail />} />
            </Route>
            <Route path="payments" element={<Outlet />}>
              <Route index element={<AgencyPayments />} />
              <Route path=":paymentId" element={<AgencyPaymentDetail />} />
            </Route>
            <Route path="commissions" element={<Outlet />}>
              <Route index element={<AgencyCommissions />} />
              <Route path=":commissionId" element={<AgencyCommissionDetail />} />
            </Route>
            <Route path="forms" element={<Outlet />}>
              <Route index element={<AgencyForms />} />
              <Route path="new" element={<AgencyFormEditor />} />
              <Route path=":formId" element={<AgencyFormEditor />} />
              <Route path=":formId/responses" element={<AgencyFormResponses />} />
              <Route path=":formId/responses/:responseId" element={<AgencyFormResponseDetail />} />
            </Route>
            <Route path="social" element={<Outlet />}>
              <Route index element={<AgencySocialList />} />
              <Route path=":templateId" element={<AgencySocialEditor />} />
            </Route>
            <Route path="blog" element={<Outlet />}>
              <Route index element={<AgencyBlogList />} />
              <Route path="new" element={<AgencyBlogEditor />} />
              <Route path=":postId" element={<AgencyBlogEditor />} />
            </Route>
            <Route path="settings" element={<AgencySettings />} />
            <Route path="team" element={<AgencyTeam />} />
          </Route>
        </Route>
      </Route>
      <Route path="public" element={<Layout />}>
        <Route path="trips/:id" element={<PublicTripLayout />}>
          <Route index element={<Navigate to="calendar" />} />
          <Route index path="calendar/:eventId?" element={<PublicTripCalendar />} />
          <Route path="map" element={<PublicTripMap />} />
        </Route>
        <Route path="agency/directory" element={<AgencyDirectory />} />
        <Route path="agency/profile/:agencyId" element={<Outlet />}>
          <Route index element={<AgencyProfile />} />
          <Route path="blog" element={<AgencyProfile />} />
          <Route path="blog/:postSlug" element={<AgencyBlogPublicDetail />} />
          <Route path="form/:formId" element={<PublicFormSubmit />} />
        </Route>
      </Route>
      <Route path="pay/:token" element={<PaymentCheckout />} />
      <Route path="pay/:token/confirmation" element={<PaymentConfirmation />} />
      <Route path="login" element={<Login />} />
    </Route>,
  ),
);

const customDomainRouter = createBrowserRouter(createRoutesFromElements(<Route path="*" element={<CustomDomainProfile />} errorElement={<ErrorPage />} />));

ReactDOM.createRoot(document.getElementById("root")).render(
  <PostHogProvider client={posthog}>
    <QueryClientProvider client={queryClient}>
      {isCustomDomain ? (
        <ChakraProvider value={defaultSystem}>
          <RouterProvider router={customDomainRouter} />
        </ChakraProvider>
      ) : (
        <APIProvider apiKey={import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY} libraries={["places"]}>
          <Authenticator.Provider>
            <ProvideAuth>
              <ChakraProvider value={defaultSystem}>
                <RouterProvider router={router} base />
                <ReactQueryDevtools initialIsOpen={false} />
              </ChakraProvider>
            </ProvideAuth>
          </Authenticator.Provider>
        </APIProvider>
      )}
    </QueryClientProvider>
  </PostHogProvider>,
);
